import { Button, ICON_COLOR } from '@zep-us/zus';
import { PlayIcon, RectanglePortraitStackFillIcon, SquareStackIcon } from '@zep-us/zus/icons';
import { useTranslation } from 'next-i18next';
import { NewChip } from '@/shared/components/Chip/NewChip';
import { CTAButtonRightSlot } from './CTAButtonRightSlot';
import { WorksheetGradientIcon } from './WorksheetGradientIcon';

/**
 * GuestCTAButtons Props
 */
export interface GuestCTAButtonsProps {
  onCopy: () => void;
  onEnter: () => void;
  onExportWorksheet: () => void;
  /** 플래시카드 학습 진입 핸들러 */
  onFlashcard: () => void;
}

/**
 * GuestCTAButtons - 게스트 사용자용 로그인 CTA 컴포넌트
 *
 * @description
 * 로그인하지 않은 게스트 사용자에게 로그인을 유도하는 CTA 섹션을 렌더링합니다.
 * Story 2.5: 게스트 사용자용 로그인 CTA
 *
 * @example
 * ```tsx
 * <GuestCTAButtons
 *   onLogin={handleLogin}
 *   t={t}
 * />
 * ```
 */
export const GuestCTAButtons = ({
  onCopy,
  onEnter,
  onExportWorksheet,
  onFlashcard
}: GuestCTAButtonsProps) => {
  const {
    t
  } = useTranslation(['common']);
  const buttonsStyle = 'group w-full justify-start px-md gap-xs';
  return <div className="flex w-full flex-col gap-xs" data-sentry-component="GuestCTAButtons" data-sentry-source-file="GuestCTAButtons.tsx">
      <Button size="md" intent="gradient" variant="contained" onClick={onCopy} leftSlot={<SquareStackIcon size="sm" fill={ICON_COLOR.inverse} />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="GuestCTAButtons.tsx">
        {t('detail.copyToMyQuiz')}
      </Button>
      <div className="flex flex-col gap-xs pt-sm">
        <p className="text-caption-lg-semibold text-alternative">
          {t('common:detail.quizSolve')}
        </p>
        <Button size="md" intent="alternative" variant="contained" onClick={onEnter} leftSlot={<PlayIcon size="sm" fill={ICON_COLOR.strong} />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="GuestCTAButtons.tsx">
          {t('detail.cta.tutorial')}
        </Button>
        {/* 2. 플래시카드 - Flashcard button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onFlashcard} leftSlot={<RectanglePortraitStackFillIcon size="sm" className="text-blue-300" />} rightSlot={<CTAButtonRightSlot badge={<NewChip className="bg-blue-50 text-blue-500" />} />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="GuestCTAButtons.tsx">
          {t('common:flashcard.label')}
        </Button>
        {/* 3. 워크시트 - Worksheet button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onExportWorksheet} leftSlot={<WorksheetGradientIcon size="sm" />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="GuestCTAButtons.tsx">
          {t('common:quizWorksheet.label')}
        </Button>
      </div>
    </div>;
};